import { Box, Flex, Progress, Text } from "@chakra-ui/react";
import { cloneDeep } from "lodash";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import IconBox from "components/icons/IconBox";
import { IconUpload } from "components/icons/Icons";
import { updateProjectStatus } from "store/customerProject/reducer";
import { uploadProjectSelector } from "store/customerProject/selector";
import TranslationFileRow from "views/customer/order_old/components/translation/TranslationFileRow";
import { DocumentType } from "views/customer/order_old/variables/orderData";
import { axiosService } from "network/axios";
import { initFilestackClient } from "network/filestack";

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
  "application/json",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/tiff",
]);

const ALLOWED_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "txt",
  "csv",
  "json",
  "png",
  "jpg",
  "jpeg",
  "tif",
  "tiff",
]);

export default function FileUploadHandler() {
  const uploadProject = useSelector(uploadProjectSelector);
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<
    { id: string; name: string; progress: number }[]
  >([]);

  const removeFile = (rowId: string) => {
    let clonedDocumnets = cloneDeep(uploadProject.documents);
    let index = clonedDocumnets?.findIndex((it) => String(it.id) === rowId);
    if (index > -1) {
      clonedDocumnets.splice(index, 1);
    }

    dispatch(
      updateProjectStatus({
        documents: [...clonedDocumnets],
      }),
    );
  };

  const uploadFromPicker = async (payload: any[]) => {
    setIsUploading(true);
    setUploadError(null);
    try {
      const { data }: any = await axiosService.post(
        `/document/upload-cdn-documents`,
        {
          payload,
        },
      );

      const uploadResult = data?.result ?? [];
      const uploadedDocuments: DocumentType[] = uploadResult?.map((it: any) => {
        const { uploadId, pageCount, wordCount } = it;
        const matchedFile = payload?.find(
          (_it: any) => _it?.uploadId === uploadId,
        );
        return {
          id: uploadId,
          pages: pageCount,
          words: wordCount,
          type: matchedFile?.type,
          fileUrl: matchedFile?.fileUrl || "",
          file: {
            name: matchedFile?.fileName,
          },
        };
      });

      dispatch(
        updateProjectStatus({
          documents: [...uploadProject.documents, ...uploadedDocuments],
        }),
      );
    } catch (err: any) {
      console.log(err);
      setUploadError(
        "Upload failed. Please try again. If the issue persists, contact support.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFiles = async (files: File[]) => {
    setIsUploading(true);
    setUploadError(null);

    const filestack = await initFilestackClient();

    const pending = files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      progress: 0,
    }));
    setUploadingFiles(pending);

    const uploaded = await Promise.all(
      files.map(async (file) => {
        const localId = `${file.name}-${file.size}-${file.lastModified}`;
        const res: any = await filestack.upload(file, {
          onProgress: (event: any) => {
            const totalPercent = Number(event?.totalPercent);
            const total = Number(event?.total ?? 0);
            const loaded = Number(event?.loaded ?? 0);
            const next = Number.isFinite(totalPercent)
              ? Math.round(totalPercent)
              : total > 0
                ? Math.round((loaded / total) * 100)
                : 0;
            setUploadingFiles((prev) =>
              prev.map((it) =>
                it.id === localId
                  ? { ...it, progress: Math.min(100, Math.max(0, next)) }
                  : it,
              ),
            );
          },
        });
        return {
          uploadId: res?.handle,
          fileUrl: res?.url,
          type: file.type,
          fileName: file.name,
        };
      }),
    );

    // At this point Filestack upload is complete for all files.
    // Hide progress rows while backend processes page/word counts.
    setUploadingFiles([]);

    try {
      const { data }: any = await axiosService.post(
        `/document/upload-cdn-documents`,
        {
          payload: uploaded,
        },
      );

      const uploadResult = data?.result ?? [];
      const uploadedDocuments: DocumentType[] = uploadResult?.map((it: any) => {
        const { uploadId, pageCount, wordCount } = it;
        const matchedFile = uploaded?.find(
          (_it: any) => _it?.uploadId === uploadId,
        );
        return {
          id: uploadId,
          pages: pageCount,
          words: wordCount,
          type: matchedFile?.type,
          fileUrl: matchedFile?.fileUrl || "",
          file: {
            name: matchedFile?.fileName,
          },
        };
      });

      dispatch(
        updateProjectStatus({
          documents: [...uploadProject.documents, ...uploadedDocuments],
        }),
      );
    } catch (err: any) {
      console.log(err);
      setUploadError(
        "Upload failed. Please try again. If the issue persists, contact support.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const validateFiles = (files: File[]) => {
    if (files.length < 1) return { ok: false, message: null as string | null };

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return {
          ok: false,
          message: "Unsupported file type or size. Max 25 MB per file",
        };
      }

      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      const typeOk = file.type ? ALLOWED_MIME_TYPES.has(file.type) : true;
      const extOk = ext ? ALLOWED_EXTENSIONS.has(ext) : true;
      if (!typeOk || !extOk) {
        return {
          ok: false,
          message: "Unsupported file type or size. Max 25 MB per file",
        };
      }
    }
    return { ok: true, message: null as string | null };
  };

  const validatePickerFiles = (filesUploaded: any[]) => {
    if (!filesUploaded || filesUploaded.length < 1) {
      return { ok: false, message: null as string | null };
    }
    for (const f of filesUploaded) {
      const size = Number(f?.size ?? 0);
      if (size > MAX_FILE_SIZE_BYTES) {
        return {
          ok: false,
          message: "Unsupported file type or size. Max 25 MB per file",
        };
      }

      const fileName = String(f?.filename ?? "");
      const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
      const mimetype = String(f?.mimetype ?? "");
      const typeOk = mimetype ? ALLOWED_MIME_TYPES.has(mimetype) : true;
      const extOk = ext ? ALLOWED_EXTENSIONS.has(ext) : true;
      if (!typeOk || !extOk) {
        return {
          ok: false,
          message: "Unsupported file type or size. Max 25 MB per file",
        };
      }
    }
    return { ok: true, message: null as string | null };
  };

  const handlePickerUpload = async (event: any) => {
    const { filesUploaded } = event;
    const { ok, message } = validatePickerFiles(filesUploaded);
    if (!ok) {
      setUploadError(message);
      return;
    }

    // Filestack picker finished uploading; hide progress rows.
    setUploadingFiles([]);

    const payload = filesUploaded?.map((it: any) => {
      return {
        uploadId: it.uploadId,
        fileUrl: it.url,
        type: it.mimetype,
        fileName: it.filename,
      };
    });

    await uploadFromPicker(payload);
  };

  const handlePickerFileUploadStarted = (file: any) => {
    setIsUploading(true);
    setUploadError(null);
    const name = String(file?.filename ?? "");
    const size = Number(file?.size ?? 0);
    const id = `${name}-${size}`;
    setUploadingFiles((prev) => {
      if (prev.some((it) => it.id === id)) return prev;
      return [...prev, { id, name, progress: 0 }];
    });
  };

  const handlePickerFileUploadProgress = (file: any, progress: any) => {
    const name = String(file?.filename ?? "");
    const size = Number(file?.size ?? 0);
    const id = `${name}-${size}`;
    const next = Math.round(Number(progress?.totalPercent ?? progress ?? 0));
    setUploadingFiles((prev) =>
      prev.map((it) =>
        it.id === id
          ? { ...it, progress: Math.min(100, Math.max(0, next)) }
          : it,
      ),
    );
  };

  const openPicker = async () => {
    if (isUploading) return;
    setUploadError(null);
    const pickerOptions: any = {
      maxFiles: 100,
      maxSize: MAX_FILE_SIZE_BYTES,
      onFileUploadStarted: handlePickerFileUploadStarted,
      onFileUploadProgress: handlePickerFileUploadProgress,
      onUploadDone: handlePickerUpload,
    };

    const filestack = await initFilestackClient();
    filestack.picker(pickerOptions).open();
  };

  const onDrop = async (acceptedFiles: File[]) => {
    const { ok, message } = validateFiles(acceptedFiles);
    if (!ok) {
      setUploadError(message);
      return;
    }
    await uploadFiles(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: () => {
      setUploadError("Unsupported file type or size. Max 25 MB per file");
    },
    multiple: true,
    disabled: isUploading,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <>
      {uploadError && (
        <Box
          bg="#FDECEC"
          borderRadius="12px"
          padding="12px"
          mb="12px"
          color="#B42318"
          fontSize="14px"
        >
          {uploadError}
        </Box>
      )}

      <Box
        {...getRootProps({
          onClick: () => {
            openPicker();
          },
        })}
        borderRadius="12px"
        border="1px dashed #BFC8D6"
        bg="#FFF"
        padding={{ base: "18px", md: "22px" }}
        cursor={isUploading ? "not-allowed" : "pointer"}
        opacity={isUploading ? 0.7 : 1}
      >
        <input {...getInputProps()} />
        <Flex direction="column" alignItems="center" justifyContent="center">
          <Box mb="10px" opacity={0.85}>
            <IconBox
              w="32px"
              h="32px"
              borderRadius="8px"
              bg="#F5F7FA"
              icon={<IconUpload w="24px" h="24px" />}
            />
          </Box>
          <Text fontSize="14px" color="#667085" textAlign="center">
            <Text as="span" color="#2F80ED" fontWeight="600">
              {isUploading ? "Uploading..." : "Click to upload"}
            </Text>{" "}
            {isDragActive ? "(drop now)" : "or drag and drop"}
          </Text>
          <Text mt="4px" fontSize="14px" color="#0B0E10" textAlign="center">
            PDF, DOC, DOCX, XLS, XLSX, TXT, CSV, PNG, JPG, JPEG, JSON
          </Text>
        </Flex>
      </Box>

      {uploadingFiles.length > 0 && (
        <Box mt="12px">
          {uploadingFiles.map((f) => (
            <Box
              key={f.id}
              border="1px solid #E0E0E0"
              borderRadius="12px"
              p="12px"
              mb="10px"
              bg="#FFF"
            >
              <Flex justifyContent="space-between" alignItems="center" mb="8px">
                <Text fontSize="14px" fontWeight="500" color="#000">
                  {f.name}
                </Text>
                <Text fontSize="12px" color="#667085">
                  {f.progress}%
                </Text>
              </Flex>
              <Progress
                value={f.progress}
                size="sm"
                borderRadius="999px"
                colorScheme="blue"
                bg="#EEF2F6"
              />
            </Box>
          ))}
        </Box>
      )}

      {uploadProject.documents?.length > 0 && (
        <Box maxH="168px" overflowY="auto" mt="12px">
          {uploadProject.documents?.map((it: DocumentType) => (
            <TranslationFileRow
              key={it?.id}
              rowData={it}
              onRemoveFile={(rowId: string) => {
                removeFile(rowId);
              }}
            />
          ))}
        </Box>
      )}
    </>
  );
}
